<template>
  <div class="col-xl-12">
    <div class="card sku-sales-card" ref="skuSalesCard">
      <!-- HEADER -->
      <div
        class="card-header card-no-border"
        style="padding-bottom: 8px !important"
      >
        <div
          class="d-flex justify-content-between align-items-start flex-wrap gap-2"
        >
          <h5>SKU WISE SALES</h5>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i
                :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"
              ></i>
            </div>
          </div>
        </div>

        <div
          class="d-flex justify-content-start align-items-start flex-wrap gap-2"
        >
          <div class="header-top">
            <div class="d-flex gap-2 flex-wrap">
              <!-- Distributor -->
              <select
                v-model="selectedDistributor"
                class="form-select"
                @focus="lazyLoadDistributors"
                @change="handleDistributorChange"
                :disabled="isDistributor || isDistributorBackOffice"
              >
                <option v-if="!isDistributor && !isDistributorBackOffice" value="">All Distributors</option>
                <option
                  v-for="d in distributors"
                  :key="d.profile_id"
                  :value="d.profile_id"
                >
                  {{ d.name }}
                </option>
              </select>

              <!-- From Date -->
              <input
                type="date"
                v-model="filterFromDate"
                class="form-control"
                style="width: 140px"
                :max="todayDate"
                @change="handleDateChange"
              />

              <!-- To Date -->
              <input
                type="date"
                v-model="filterToDate"
                class="form-control"
                style="width: 140px"
                :max="todayDate"
                @change="handleDateChange"
              />

              <!-- SKU (multi-select) -->
              <div
                class="sku-multiselect"
                ref="skuMultiSelect"
                @focus="lazyLoadItems"
              >
                <button
                  type="button"
                  class="form-select sku-select multiselect-toggle"
                  @click="toggleSkuDropdown"
                  @focus="lazyLoadItems"
                >
                  <span class="multiselect-toggle-label">{{ skuSelectLabel }}</span>
                  <i
                    class="fa fa-chevron-down multiselect-caret"
                    :class="{ open: skuDropdownOpen }"
                  ></i>
                </button>

                <div v-if="skuDropdownOpen" class="sku-dropdown-panel">
                  <label class="sku-option sku-option-all">
                    <input
                      type="checkbox"
                      :checked="selectedSkus.length === 0"
                      @change="clearSkuSelection"
                    />
                    <span>All SKUs</span>
                  </label>

                  <div class="sku-option-divider"></div>

                  <label
                    v-for="item in items"
                    :key="item.id"
                    class="sku-option"
                  >
                    <input
                      type="checkbox"
                      :value="item.id"
                      v-model="selectedSkus"
                      @change="handleSkuChange"
                    />
                    <span>{{ item.item_name || item.description }}</span>
                  </label>

                  <div v-if="!items.length" class="sku-option-empty">
                    No SKUs found
                  </div>
                </div>
              </div>

              <!-- Measure -->
              <select
                v-model="selectedMeasure"
                class="form-select"
                @change="handleMeasureChange"
              >
                <option v-if="!isAnySelectedSkuQtyOnly" value="mt">
                  Volume (MT)
                </option>
                <option value="qty">Quantity (Qty)</option>
              </select>

              <router-link
                :to="{
                  name: 'SkuWiseSalesDetail',
                  query: {
                    name: selectedDistributor,
                    from_date: filterFromDate,
                    to_date: filterToDate,
                    sku: selectedSkus.join(','),
                    measure: selectedMeasure,
                  },
                }"
                target="_blank"
                class="btn btn-sm view-more-btn"
              >
                View Details
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- BODY -->
      <div class="card-body" :class="loading ? 'loading-center' : 'pt-0'">
        <!-- LOADING -->
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-container">
          <img
            src="../../../../assets/vectors/data_not_found.png"
            alt="Error"
            class="error-image"
          />
          <p class="error-message">{{ error }}</p>
        </div>

        <!-- CONTENT -->
        <div v-show="!loading && !error">
          <!-- Chart -->
          <div v-if="showFullscreenHint" class="preview-hint-tag">
            <i class="fa fa-expand"></i>
            <span>{{ fullscreenHintText }}</span>
          </div>

          <div class="chart-frame">
            <!-- Fixed x-axis title + legend - fullscreen only -->
            <div
              v-if="isFullScreen && series.length > 0"
              class="chart-fixed-controls"
            >
              <div class="chart-fixed-x-title">
                {{ xAxisTitle }}
              </div>

              <div class="chart-fixed-legend">
                <button
                  v-for="(item, index) in fullscreenLegendSeries"
                  :key="item.name"
                  type="button"
                  class="chart-legend-pill"
                  :class="{ disabled: isSeriesDisabled(item.name) }"
                  @click="toggleSeries(item.name)"
                >
                  <span
                    class="chart-legend-dot"
                    :style="{ backgroundColor: getSeriesColor(index) }"
                  ></span>
                  <span class="chart-legend-name">{{ item.name }}</span>
                </button>
              </div>
            </div>

            <div class="chart-body-frame">
              <!-- Fixed Y Axis - fullscreen only -->
              <div
                v-if="isFullScreen && chartRenderSeries.length > 0"
                class="fixed-y-axis"
              >
                <div class="fixed-y-axis-title">
                  {{ fixedYAxisTitle }}
                </div>

                <div class="fixed-y-axis-labels">
                  <span v-for="tick in fixedYAxisTicks" :key="tick">
                    {{ formatFixedYAxisTick(tick) }}
                  </span>
                </div>
              </div>

              <!-- Scrollable / virtualized chart area -->
              <div
                ref="skuChartScroller"
                class="chart-container"
                :class="{
                  'has-horizontal-scroll':
                    isFullScreen && chartHasHorizontalScroll,
                }"
                @scroll="handleChartScroll"
              >
                <!-- Fullscreen virtual scroll width, but Apex renders only visible slice -->
                <div
                  v-if="isFullScreen"
                  class="chart-virtual-space"
                  :style="{ width: chartVirtualWidth }"
                >
                  <div
                    class="chart-render-window"
                    :style="{ width: chartRenderWidth }"
                  >
                    <apexchart
                      v-if="chartReady && chartRenderSeries.length > 0"
                      :key="chartKey"
                      :height="chartHeight"
                      width="100%"
                      type="bar"
                      :options="chartOptions"
                      :series="chartRenderSeries"
                    />
                  </div>
                </div>

                <!-- Normal non-fullscreen chart -->
                <div v-else class="chart-scroll-inner">
                  <apexchart
                    v-if="chartReady && chartRenderSeries.length > 0"
                    :key="chartKey"
                    :height="chartHeight"
                    width="100%"
                    type="bar"
                    :options="chartOptions"
                    :series="chartRenderSeries"
                  />
                </div>

                <!-- Scroll hint -->
                <div
                  v-if="
                    isFullScreen && chartHasHorizontalScroll && showScrollHint
                  "
                  class="chart-scroll-hint"
                >
                  <i class="fa fa-hand-pointer scroll-hand"></i>
                  <span>Drag sideways for more</span>
                  <i class="fa fa-arrow-right scroll-arrow"></i>
                </div>
              </div>

              <div
                v-if="isFullScreen && isVirtualRendering"
                class="chart-render-loader"
              >
                <div class="chart-render-loader-card">
                  <span class="chart-render-loader-dot"></span>
                  <span>Rendering visible chart...</span>
                </div>
              </div>
            </div>
          </div>
          <!-- /Chart -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import {
  fetchDistributors,
  fetchItemNameList,
  fetchSkuWiseSalesStats,
} from "@/services/dataService";

export default {
  components: {
    apexchart: VueApexCharts,
  },

  data() {
    const defaultDate = new Date().toISOString().split("T")[0];

    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: localStorage.getItem("user_label") === "DISTRIBUTOR",
      isDistributorBackOffice: localStorage.getItem("user_label") === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,

      chartHeight: 350,
      chartKey: 0,
      chartReady: true,

      fullscreenChangeHandler: null,
      skuDropdownClickOutsideHandler: null,
      distributorsLoaded: false,
      itemsLoaded: false,

      distributors: [],
      items: [],

      selectedDistributor: "",

      selectedFromDate: defaultDate,
      selectedToDate: defaultDate,

      filterFromDate: defaultDate,
      filterToDate: defaultDate,

      // Multi-select SKU state
      selectedSkus: [],
      skuDropdownOpen: false,

      selectedMeasure: "qty",

      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],

      rawData: [],
      series: [],

      // Fullscreen performance states
      disabledSeriesNames: [],
      chartViewportWidth: 1200,
      virtualStartIndex: 0,
      virtualWindowSize: 42,
      virtualScrollRaf: null,
      virtualRenderTimer: null,
      isVirtualRendering: false,

      fullscreenPreparing: false,
      fullscreenMaxVisibleSeries: 8,

      chartHasHorizontalScroll: false,
      showScrollHint: false,
      scrollHintTimer: null,
    };
  },

  async mounted() {
    this.loading = true;
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }

    await this.lazyLoadItems();

    this.safeReloadData();

    this.fullscreenChangeHandler = async () => {
      const isFS = !!document.fullscreenElement;

      this.isFullScreen = isFS;

      if (isFS) {
        this.fullscreenPreparing = true;
        this.isVirtualRendering = false;
      }

      if (!isFS) {
        this.disabledSeriesNames = [];
        this.virtualStartIndex = 0;
        this.virtualWindowSize = 42;
        this.isVirtualRendering = false;
        this.fullscreenPreparing = false;
      }

      await this.remountChartOnly();

      if (isFS) {
        setTimeout(() => {
          this.fullscreenPreparing = false;
        }, 350);
      }
    };
    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);

    this.skuDropdownClickOutsideHandler = (event) => {
      const el = this.$refs.skuMultiSelect;
      if (this.skuDropdownOpen && el && !el.contains(event.target)) {
        this.skuDropdownOpen = false;
      }
    };
    document.addEventListener("click", this.skuDropdownClickOutsideHandler);
  },

  beforeUnmount() {
    if (this.fullscreenChangeHandler) {
      document.removeEventListener(
        "fullscreenchange",
        this.fullscreenChangeHandler,
      );
    }

    if (this.skuDropdownClickOutsideHandler) {
      document.removeEventListener(
        "click",
        this.skuDropdownClickOutsideHandler,
      );
    }

    if (this.scrollHintTimer) {
      clearTimeout(this.scrollHintTimer);
      this.scrollHintTimer = null;
    }

    if (this.virtualScrollRaf) {
      cancelAnimationFrame(this.virtualScrollRaf);
      this.virtualScrollRaf = null;
    }

    if (this.virtualRenderTimer) {
      clearTimeout(this.virtualRenderTimer);
      this.virtualRenderTimer = null;
    }
  },

  computed: {
    todayDate() {
      return new Date().toISOString().split("T")[0];
    },

    selectedSkuItems() {
      if (!this.selectedSkus.length) return [];

      return this.items.filter((item) =>
        this.selectedSkus.some(
          (skuId) => String(item.id ?? item.variant_id) === String(skuId),
        ),
      );
    },

    isAnySelectedSkuQtyOnly() {
      if (!this.selectedSkus.length) return false;

      return this.selectedSkuItems.some(
        (item) =>
          Number(item?.is_cylinder) === 0 || item?.allowed_measures === "qty",
      );
    },

    skuSelectLabel() {
      if (!this.selectedSkus.length) return "All SKUs";

      if (this.selectedSkus.length === 1) {
        const item = this.selectedSkuItems[0];
        return item ? item.item_name || item.description : "1 SKU selected";
      }

      return `${this.selectedSkus.length} SKUs selected`;
    },

    dateRange() {
      const dates = [];
      if (!this.selectedFromDate || !this.selectedToDate) return dates;

      const [fy, fm, fd] = this.selectedFromDate.split("-").map(Number);
      const [ty, tm, td] = this.selectedToDate.split("-").map(Number);

      const cur = new Date(fy, fm - 1, fd);
      const end = new Date(ty, tm - 1, td);

      while (cur <= end) {
        const y = cur.getFullYear();
        const m = String(cur.getMonth() + 1).padStart(2, "0");
        const d = String(cur.getDate()).padStart(2, "0");
        dates.push(`${y}-${m}-${d}`);
        cur.setDate(cur.getDate() + 1);
      }

      return dates;
    },

    rangeDayCount() {
      return this.dateRange.length;
    },

    previewAggregationMode() {
      if (this.isFullScreen) return "day";

      if (this.rangeDayCount <= 21) return "day";

      if (this.rangeDayCount <= 93) return "week";

      return "month";
    },

    chartBuckets() {
      const bucketMap = new Map();

      this.dateRange.forEach((dateValue) => {
        const bucket = this.getChartBucket(dateValue);

        if (bucket && !bucketMap.has(bucket.key)) {
          bucketMap.set(bucket.key, bucket);
        }
      });

      return Array.from(bucketMap.values());
    },

    xCategories() {
      return this.chartBuckets.map((bucket) => bucket.label);
    },

    xAxisGroups() {
      if (this.previewAggregationMode !== "week" || this.isFullScreen) {
        return [];
      }

      const groups = [];

      this.chartBuckets.forEach((bucket) => {
        const groupLabel = bucket.groupLabel || "";
        const lastGroup = groups[groups.length - 1];

        if (lastGroup && lastGroup.title === groupLabel) {
          lastGroup.cols += 1;
        } else {
          groups.push({
            title: groupLabel,
            cols: 1,
          });
        }
      });

      return groups;
    },

    xAxisTitle() {
      if (this.isFullScreen) return "Day of Month";

      if (this.previewAggregationMode === "week") {
        return "Week";
      }

      if (this.previewAggregationMode === "month") {
        return "Month";
      }

      return "Day of Month";
    },

    totalQty() {
      return this.rawData.reduce((s, d) => s + (d.total_qty || 0), 0);
    },

    totalMT() {
      return parseFloat(
        this.rawData
          .reduce((s, d) => s + (d.total_metric_tons || 0), 0)
          .toFixed(3),
      );
    },

    avgDailyMT() {
      if (!this.rangeDayCount) return 0;
      return (this.totalMT / this.rangeDayCount).toFixed(3);
    },

    avgDailyQty() {
      if (!this.rangeDayCount) return 0;
      return (this.totalQty / this.rangeDayCount).toFixed(1);
    },

    activeSKUCount() {
      return new Set(this.rawData.map((d) => d.item_id)).size;
    },

    peakDay() {
      if (!this.rawData.length) return "—";

      const valueKey =
        this.selectedMeasure === "mt" ? "total_metric_tons" : "total_qty";
      const dayTotals = {};

      this.rawData.forEach((d) => {
        dayTotals[d.day] = (dayTotals[d.day] || 0) + (d[valueKey] || 0);
      });

      const peak = Object.entries(dayTotals).sort((a, b) => b[1] - a[1])[0];

      return peak ? `Day ${peak[0]}` : "—";
    },

    chartOptions() {
      const isMT = this.selectedMeasure === "mt";
      const measureLabel = isMT ? "Volume (MT)" : "Quantity (Qty)";
      const measureSuffix = isMT ? "MT" : "Qty";

      return {
        chart: {
          type: "bar",
          height: this.chartHeight,
          toolbar: { show: false },
          zoom: { enabled: false },
          stacked: false,
          parentHeightOffset: 0,
          redrawOnWindowResize: false,
          redrawOnParentResize: false,
          animations: {
            enabled: false,
          },
        },

        plotOptions: {
          bar: {
            borderRadius: this.isFullScreen ? 6 : 3,
            columnWidth: this.isFullScreen ? "78%" : "48%",
          },
        },

        dataLabels: {
          enabled: false,
        },

        stroke: {
          show: true,
          width: 1,
          colors: ["transparent"],
        },

        xaxis: {
          categories: this.chartRenderCategories,

          group: {
            groups: this.xAxisGroups,
            style: {
              colors: ["#64748b"],
              fontSize: "11px",
              fontWeight: 700,
              cssClass: "apexcharts-xaxis-group-label",
            },
          },

          title: {
            text: this.isFullScreen ? "" : this.xAxisTitle,
            offsetY: this.isFullScreen ? 4 : 0,
            style: {
              fontSize: this.isFullScreen ? "12px" : "11px",
              fontWeight: 700,
              color: "#64748b",
            },
          },

          labels: {
            show: true,
            rotate: this.isFullScreen ? -20 : 0,
            hideOverlappingLabels: false,
            trim: true,
            style: {
              fontSize: this.isFullScreen ? "11px" : "11px",
              colors: "#64748b",
              fontWeight: 700,
            },
          },
        },

        yaxis: {
          min: 0,
          max: this.fixedYAxisMax,
          tickAmount: 5,

          title: {
            text: this.isFullScreen ? "" : measureLabel,
            offsetX: 0,
            style: {
              fontSize: this.isFullScreen ? "12px" : "11px",
              fontWeight: 700,
              color: "#64748b",
            },
          },

          labels: {
            show: !this.isFullScreen,
            formatter: (val) => this.formatTruncatedTwoDecimals(val),
            style: {
              fontSize: this.isFullScreen ? "12px" : "11px",
              colors: "#64748b",
            },
          },
        },

        legend: {
          show: !this.isFullScreen,
          position: "top",
          horizontalAlign: "left",
          fontSize: "12px",
          markers: {
            width: 8,
            height: 8,
            radius: 8,
          },
        },

        tooltip: {
          shared: this.chartRenderSeries.length <= 12,
          intersect: false,
          y: {
            formatter: (val) =>
              `${this.formatTruncatedTwoDecimals(val)} ${measureSuffix}`,
          },
        },

        grid: {
          show: true,
          borderColor: "#eef2f7",
          padding: {
            top: this.isFullScreen ? 12 : 6,
            right: this.isFullScreen ? 18 : 12,
            bottom: this.isFullScreen ? 46 : 6,
            left: this.isFullScreen ? 0 : 8,
          },
        },

        colors: [
          "#6c5ce7",
          "#00b894",
          "#0984e3",
          "#e17055",
          "#fdcb6e",
          "#fd79a8",
          "#00cec9",
          "#a29bfe",
          "#55efc4",
          "#fab1a0",
        ],
      };
    },

    selectedRangeMonthCount() {
      if (!this.selectedFromDate || !this.selectedToDate) return 0;

      const [fromYear, fromMonth] = this.selectedFromDate
        .split("-")
        .map(Number);
      const [toYear, toMonth] = this.selectedToDate.split("-").map(Number);

      return (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;
    },

    chartCategoryCount() {
      return Array.isArray(this.series) ? this.series.length : 0;
    },

    showFullscreenHint() {
      if (this.isFullScreen || this.loading || this.error) return false;

      return this.selectedRangeMonthCount > 4 || this.chartCategoryCount > 6;
    },

    fullscreenHintText() {
      const hasLargeRange = this.selectedRangeMonthCount > 4;
      const hasManyCategories = this.chartCategoryCount > 6;

      if (hasLargeRange && hasManyCategories) {
        return "Large date range and multiple SKUs detected. Use fullscreen view for clearer analysis.";
      }

      if (hasLargeRange) {
        return "Large date range detected. Use fullscreen view for clearer analysis.";
      }

      return "Multiple SKU categories detected. Use fullscreen view for clearer analysis.";
    },

    seriesWithTotals() {
      return this.series
        .map((item, index) => ({
          ...item,
          originalIndex: index,
          total: Array.isArray(item.data)
            ? item.data.reduce((sum, value) => sum + Number(value || 0), 0)
            : 0,
        }))
        .sort((a, b) => b.total - a.total);
    },

    fullscreenCandidateSeries() {
      if (!this.isFullScreen) {
        return this.seriesWithTotals;
      }

      if (this.selectedSkus.length > 0) {
        return this.seriesWithTotals;
      }

      return this.seriesWithTotals.slice(0, this.fullscreenMaxVisibleSeries);
    },

    fullscreenLegendSeries() {
      if (!this.isFullScreen) {
        return this.series;
      }

      return this.fullscreenCandidateSeries;
    },

    activeFullSeries() {
      const baseSeries = this.isFullScreen
        ? this.fullscreenCandidateSeries
        : this.seriesWithTotals;

      return baseSeries.filter(
        (item) => !this.disabledSeriesNames.includes(item.name),
      );
    },

    yAxisRawMax() {
      const values = [];

      this.activeFullSeries.forEach((item) => {
        if (Array.isArray(item.data)) {
          item.data.forEach((value) => {
            const numberValue = Number(value);
            if (Number.isFinite(numberValue)) {
              values.push(numberValue);
            }
          });
        }
      });

      return values.length ? Math.max(...values) : 0;
    },

    fixedYAxisMax() {
      const max = this.yAxisRawMax;

      if (!max || max <= 0) return 5;

      const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
      const normalized = max / magnitude;

      let niceNormalized = 10;

      if (normalized <= 1) {
        niceNormalized = 1;
      } else if (normalized <= 2) {
        niceNormalized = 2;
      } else if (normalized <= 5) {
        niceNormalized = 5;
      }

      return niceNormalized * magnitude;
    },

    fixedYAxisTicks() {
      const tickCount = 5;
      const max = this.fixedYAxisMax;
      const step = max / tickCount;

      return Array.from({ length: tickCount + 1 }, (_, index) => {
        return max - step * index;
      });
    },

    fixedYAxisTitle() {
      return this.selectedMeasure === "mt" ? "Volume (MT)" : "Quantity (Qty)";
    },

    chartBucketWidth() {
      if (!this.isFullScreen) return 58;

      const hasSkuFilter = this.selectedSkus.length > 0;

      if (this.previewAggregationMode === "day") {
        return hasSkuFilter ? 58 : 92;
      }

      if (this.previewAggregationMode === "week") {
        return hasSkuFilter ? 90 : 125;
      }

      return hasSkuFilter ? 120 : 150;
    },

    chartVirtualWidth() {
      if (!this.isFullScreen) return "100%";

      const totalWidth = this.chartBuckets.length * this.chartBucketWidth;

      return `${Math.max(this.chartViewportWidth, totalWidth)}px`;
    },

    virtualEndIndex() {
      return Math.min(
        this.chartBuckets.length,
        this.virtualStartIndex + this.virtualWindowSize,
      );
    },

    chartRenderBuckets() {
      if (!this.isFullScreen) {
        return this.chartBuckets;
      }

      return this.chartBuckets.slice(
        this.virtualStartIndex,
        this.virtualEndIndex,
      );
    },

    chartRenderCategories() {
      return this.chartRenderBuckets.map((bucket) => bucket.label);
    },

    chartRenderWidth() {
      if (!this.isFullScreen) return "100%";

      const visibleCount = this.chartRenderBuckets.length || 1;
      const width = visibleCount * this.chartBucketWidth;

      return `${Math.max(this.chartViewportWidth, width)}px`;
    },

    chartRenderSeries() {
      if (!this.isFullScreen) {
        return this.activeFullSeries.map(
          ({ total, originalIndex, ...item }) => item,
        );
      }

      return this.activeFullSeries.map(({ total, originalIndex, ...item }) => ({
        ...item,
        data: item.data.slice(this.virtualStartIndex, this.virtualEndIndex),
      }));
    },

    firstDataBucketIndex() {
      if (!this.series.length) return 0;

      for (let index = 0; index < this.chartBuckets.length; index++) {
        const hasValue = this.series.some((item) => {
          const value = Number(item.data?.[index] || 0);
          return Number.isFinite(value) && value > 0;
        });

        if (hasValue) return index;
      }

      return 0;
    },

    isSameMonthRange() {
      if (!this.selectedFromDate || !this.selectedToDate) return false;

      const [fromYear, fromMonth] = this.selectedFromDate.split("-").map(Number);
      const [toYear, toMonth] = this.selectedToDate.split("-").map(Number);

      return fromYear === toYear && fromMonth === toMonth;
    },
  },

  methods: {
    async lazyLoadDistributors() {
      if (this.distributorsLoaded) return;
      await this.fetchDistributorList();
      this.distributorsLoaded = true;
    },

    async lazyLoadItems() {
      if (this.itemsLoaded) return;
      await this.loadItems();
      this.itemsLoaded = true;
    },

    async safeReloadData(beforeFetch = null) {
      this.chartReady = false;
      await this.$nextTick();

      if (typeof beforeFetch === "function") {
        beforeFetch();
      }

      this.virtualStartIndex = 0;
      this.virtualWindowSize = 42;
      this.isVirtualRendering = false;

      await this.fetchStats();

      this.updateChartHeightForMode();

      await this.$nextTick();

      this.chartKey += 1;
      this.chartReady = true;

      await this.$nextTick();
      this.refreshChartScrollState();
    },

    async remountChartOnly() {
      this.chartReady = false;
      await this.$nextTick();

      this.updateChartHeightForMode();

      if (this.rawData.length > 0) {
        this.buildChartSeries();
      }

      await this.$nextTick();

      this.chartKey += 1;
      this.chartReady = true;

      await this.$nextTick();

      this.refreshChartScrollState();
    },

    updateChartHeightForMode() {
      if (this.isFullScreen) {
        const card = this.$refs.skuSalesCard;
        const headerH = card?.querySelector(".card-header")?.offsetHeight || 0;
        const statH = card?.querySelector(".stat-cards-row")?.offsetHeight || 0;
        const controlsH =
          card?.querySelector(".chart-fixed-controls")?.offsetHeight || 0;

        const available =
          window.innerHeight - headerH - statH - controlsH - 132;

        this.chartHeight = Math.max(360, available);
      } else {
        this.chartHeight = 350;
      }
    },

    async handleRefresh() {
      await this.safeReloadData();
    },

    async handleDistributorChange() {
      await this.safeReloadData();
    },

    async handleDateChange() {
      if (!this.filterFromDate || !this.filterToDate) return;

      // Guard against manually-typed future dates that bypass the max attribute
      if (this.filterFromDate > this.todayDate) {
        this.filterFromDate = this.todayDate;
      }
      if (this.filterToDate > this.todayDate) {
        this.filterToDate = this.todayDate;
      }

      await this.safeReloadData(() => {
        this.selectedFromDate = this.filterFromDate;
        this.selectedToDate = this.filterToDate;
      });
    },

    async fetchDistributorList() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        this.distributors = res.data.data;
      } catch (err) {
        if (err.response?.status === 401) return;
        console.error(err);
      }
    },

    async loadItems() {
      try {
        const companyId = localStorage.getItem("company_id");
        const res = await fetchItemNameList(companyId, 1, this.token);

        this.items = res.data?.data || [];
        this.selectedSkus = this.items
          .filter((item) => Number(item.is_cylinder) === 1)
          .map((item) => item.id ?? item.variant_id);

        this.applySkuMeasureRule();
      } catch (err) {
        if (err.response?.status === 401) return;
        console.error(err);
      }
    },

    async fetchStats() {
      try {
        this.loading = true;
        this.error = null;

        const payload = {
          profile_id: this.selectedDistributor || this.profileId,
          from_date: this.selectedFromDate,
          to_date: this.selectedToDate,
          // Comma-separated list of selected SKU ids, or "" for all SKUs.
          // Adjust this key/shape to match your backend contract if it
          // expects an array (e.g. sku_ids: this.selectedSkus) instead.
          sku_id: this.selectedSkus.length ? this.selectedSkus.join(",") : "",
        };

        const res = await fetchSkuWiseSalesStats(payload, this.token);
        const result = Array.isArray(res.data?.data) ? res.data.data : [];

        if (res.data?.success && result.length > 0) {
          this.rawData = result;
          this.buildChartSeries();
        } else {
          this.rawData = [];
          this.series = [];
          this.error = "No data available";
        }
      } catch (e) {
        console.error(e);
        this.rawData = [];
        this.series = [];
        this.error = "Something went wrong";
      } finally {
        this.loading = false;
      }
    },

    applySkuMeasureRule() {
      if (this.isAnySelectedSkuQtyOnly) {
        this.selectedMeasure = "qty";
      }
    },

    toggleSkuDropdown() {
      this.skuDropdownOpen = !this.skuDropdownOpen;
    },

    clearSkuSelection() {
      this.selectedSkus = [];
      this.handleSkuChange();
    },

    async handleSkuChange() {
      await this.safeReloadData(() => {
        this.applySkuMeasureRule();
      });
    },

    async handleMeasureChange() {
      await this.safeReloadData(() => {
        this.applySkuMeasureRule();
      });
    },

    normalizeDate(val) {
      return String(val).split("T")[0].split(" ")[0];
    },

    toDateOnly(value) {
      const cleanDate = this.normalizeDate(value);
      const [year, month, day] = cleanDate.split("-").map(Number);

      return new Date(year, month - 1, day);
    },

    getWeekOfMonth(date) {
      return Math.ceil(date.getDate() / 7);
    },

    getChartBucket(dateValue) {
      const date = this.toDateOnly(dateValue);

      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const monthShortLabel = date.toLocaleString("en-US", { month: "short" });
      const monthLongLabel = date.toLocaleString("en-US", { month: "long" });

      if (this.previewAggregationMode === "month") {
        return {
          key: `${year}-${month}`,
          label: `${monthShortLabel} ${year}`,
          groupLabel: "",
        };
      }

      if (this.previewAggregationMode === "week") {
        const weekNo = this.getWeekOfMonth(date);

        return {
          key: `${year}-${month}-W${weekNo}`,
          label: `W${weekNo}`,
          groupLabel: monthLongLabel,
        };
      }

      return {
        key: `${year}-${month}-${day}`,
        label: this.isSameMonthRange
          ? `${date.getDate()}`
          : `${monthShortLabel} ${date.getDate()}`,
        groupLabel: "",
      };
    },

    // Always groups by item_id. This naturally supports 0, 1, or many
    // selected SKUs (rawData is already filtered server-side by sku_id).
    buildChartSeries() {
      const buckets = this.chartBuckets;
      const valueKey =
        this.selectedMeasure === "mt" ? "total_metric_tons" : "total_qty";

      const bucketIndexMap = new Map(
        buckets.map((bucket, index) => [bucket.key, index]),
      );

      const skuMap = {};

      this.rawData.forEach((item) => {
        if (!skuMap[item.item_id]) {
          skuMap[item.item_id] = {
            name: item.item_name,
            data: Array(buckets.length).fill(0),
          };
        }

        const bucket = this.getChartBucket(item.date);

        if (!bucket) return;

        const index = bucketIndexMap.get(bucket.key);

        if (index !== undefined) {
          skuMap[item.item_id].data[index] += Number(item[valueKey] || 0);
        }
      });

      this.series = Object.values(skuMap);
    },

    updateChartData() {
      if (this.rawData.length > 0) {
        this.buildChartSeries();
      }
    },

    formatTruncatedTwoDecimals(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return "0.00";
      }

      const truncatedValue = Math.trunc(numberValue * 100) / 100;

      return truncatedValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    toggleFullScreen() {
      const el = this.$refs.skuSalesCard;

      if (!document.fullscreenElement) {
        el?.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },

    handleChartScroll() {
      if (this.showScrollHint) {
        this.showScrollHint = false;
      }

      if (!this.isFullScreen) return;

      if (this.virtualRenderTimer) {
        clearTimeout(this.virtualRenderTimer);
      }

      this.isVirtualRendering = true;

      this.virtualRenderTimer = setTimeout(() => {
        this.updateVirtualChartWindow();

        requestAnimationFrame(() => {
          this.isVirtualRendering = false;
        });
      }, 160);
    },

    updateVirtualChartWindow() {
      const container = this.$refs.skuChartScroller;

      if (!container) return;

      const buffer = 3;
      const viewportWidth = container.clientWidth || 1200;
      const scrollLeft = container.scrollLeft || 0;

      this.chartViewportWidth = viewportWidth;

      const firstVisibleIndex = Math.floor(scrollLeft / this.chartBucketWidth);
      const visibleCount = Math.ceil(viewportWidth / this.chartBucketWidth);

      const nextStartIndex = Math.max(0, firstVisibleIndex - buffer);
      const nextWindowSize = Math.min(
        this.chartBuckets.length,
        visibleCount + buffer * 2,
      );

      if (
        nextStartIndex !== this.virtualStartIndex ||
        nextWindowSize !== this.virtualWindowSize
      ) {
        this.virtualStartIndex = nextStartIndex;
        this.virtualWindowSize = nextWindowSize;
      }
    },
    toggleSeries(seriesName) {
      const exists = this.disabledSeriesNames.includes(seriesName);

      if (exists) {
        this.disabledSeriesNames = this.disabledSeriesNames.filter(
          (name) => name !== seriesName,
        );
        return;
      }

      this.disabledSeriesNames = [...this.disabledSeriesNames, seriesName];
    },

    isSeriesDisabled(seriesName) {
      return this.disabledSeriesNames.includes(seriesName);
    },

    getSeriesColor(index) {
      const colors = [
        "#6c5ce7",
        "#00b894",
        "#0984e3",
        "#e17055",
        "#fdcb6e",
        "#fd79a8",
        "#00cec9",
        "#a29bfe",
        "#55efc4",
        "#fab1a0",
      ];

      return colors[index % colors.length];
    },

    formatFixedYAxisTick(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return this.selectedMeasure === "mt" ? "0.00" : "0";
      }

      if (this.selectedMeasure === "mt") {
        return this.formatTruncatedTwoDecimals(numberValue);
      }

      return Math.round(numberValue).toLocaleString("en-US");
    },

    refreshChartScrollState() {
      this.$nextTick(() => {
        const container = this.$refs.skuChartScroller;

        if (!container) return;

        this.chartViewportWidth = container.clientWidth || 1200;

        const fullWidth = this.chartBuckets.length * this.chartBucketWidth;
        const hasScroll = fullWidth > container.clientWidth + 8;

        this.chartHasHorizontalScroll = this.isFullScreen && hasScroll;

        if (!this.isFullScreen) {
          this.showScrollHint = false;
          this.virtualStartIndex = 0;
          this.isVirtualRendering = false;
          return;
        }

        if (container.scrollLeft === 0 && this.firstDataBucketIndex > 0) {
          const targetIndex = Math.max(0, this.firstDataBucketIndex - 2);
          container.scrollLeft = targetIndex * this.chartBucketWidth;
        }

        this.updateVirtualChartWindow();

        if (hasScroll) {
          this.showScrollHint = true;

          if (this.scrollHintTimer) {
            clearTimeout(this.scrollHintTimer);
          }

          this.scrollHintTimer = setTimeout(() => {
            this.showScrollHint = false;
          }, 4300);
        }
      });
    },
  },
};
</script>

<style scoped>
.chart-frame {
  position: relative;
  width: 100%;
  max-width: 100%;
}

.chart-body-frame {
  position: relative;
  width: 100%;
  max-width: 100%;
}

.chart-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.chart-scroll-inner {
  width: 100%;
  min-width: 100%;
}

.spinner-container {
  display: flex;
  justify-content: center;
  height: 150px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
}

.error-image {
  max-width: 200px;
  display: block;
  margin: 0 auto;
}

.error-message {
  font-size: 15px;
  font-weight: bold;
  color: #198754;
  padding-top: 10px;
}

.header-top {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: end;
}

select {
  width: auto;
  font-size: 15px;
}

.sku-select {
  width: 170px !important;
}

select option {
  font-size: 15px;
}

.refresh-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
}

.refresh-icon:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
}

.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

.stat-cards-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.stat-card {
  flex: 1;
  min-width: 160px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #636e72;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #6c5ce7;
  line-height: 1.1;
}

.view-more-btn {
  background-color: #000;
  border: 1px solid #000;
  color: #fff;
}

/* SKU multi-select */
.sku-multiselect {
  position: relative;
}

.multiselect-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  background-color: #fff;
  cursor: pointer;
}

.multiselect-toggle-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect-caret {
  font-size: 10px;
  color: #6c757d;
  transition: transform 0.15s ease;
  flex: 0 0 auto;
}

.multiselect-caret.open {
  transform: rotate(180deg);
}

.sku-dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 60;
  width: 220px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
}

.sku-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 13.5px;
  color: #334155;
  cursor: pointer;
}

.sku-option:hover {
  background: #f1f5f9;
}

.sku-option input[type="checkbox"] {
  flex: 0 0 auto;
  cursor: pointer;
}

.sku-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sku-option-all {
  font-weight: 700;
}

.sku-option-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 2px;
}

.sku-option-empty {
  padding: 10px 8px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
}

.sku-sales-card {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;
}

.sku-sales-card * {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;
}

.sku-sales-card ::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}

.sku-sales-card ::-webkit-scrollbar-track {
  background: transparent;
}

.sku-sales-card ::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.45);
  border-radius: 999px;
}

.sku-sales-card ::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.65);
}

.sku-sales-card ::-webkit-scrollbar-corner {
  background: transparent;
}

/* Fullscreen layout */
.sku-sales-card:fullscreen {
  width: 100vw;
  height: 100vh;
  padding: 14px;
  background: #f8fafc;
  overflow: hidden;
}

.sku-sales-card:fullscreen .card-header {
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
  border: 1px solid #e8edf4;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.055);
  margin-bottom: 10px;
}

.sku-sales-card:fullscreen .card-body {
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  padding: 12px !important;
  min-height: calc(100vh - 150px);
  overflow: hidden;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.055);
}

.sku-sales-card:fullscreen .stat-cards-row {
  margin-bottom: 12px;
}

.sku-sales-card:fullscreen .stat-card {
  border-radius: 13px;
  padding: 11px 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.sku-sales-card:fullscreen .form-select,
.sku-sales-card:fullscreen .form-control {
  height: 33px;
  min-height: 33px;
  border-radius: 9px;
}

/* Fullscreen fixed controls */
.chart-fixed-controls {
  display: none;
}

.sku-sales-card:fullscreen .chart-fixed-controls {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  margin-bottom: 8px;
  padding: 0 2px;
  overflow: hidden;
}

.chart-fixed-x-title {
  flex: 0 0 auto;
  min-width: 92px;
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
  line-height: 1;
  white-space: nowrap;
}

.chart-fixed-legend {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  flex-wrap: wrap;
  min-width: 0;
  max-height: 58px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.chart-legend-pill {
  height: 26px;
  max-width: 190px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 750;
  line-height: 1;
  cursor: pointer;
  transition: all 0.18s ease;
}

.chart-legend-pill:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.chart-legend-pill.disabled {
  opacity: 0.42;
  filter: grayscale(1);
}

.chart-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.chart-legend-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fullscreen fixed y-axis layout */
.sku-sales-card:fullscreen .chart-body-frame {
  position: relative;
  width: 100%;
  max-width: 100%;
  padding-left: 74px;
}

.fixed-y-axis {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 22px;
  width: 74px;
  z-index: 35;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  border-radius: 13px 0 0 13px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  pointer-events: none;
  box-shadow: none !important;
}

.fixed-y-axis-title {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 9px;
  color: #64748b;
  font-size: 11px;
  font-weight: 850;
  line-height: 1;
  white-space: nowrap;
}

.fixed-y-axis-labels {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding: 22px 9px 66px 0;
}

.fixed-y-axis-labels span {
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  line-height: 1;
  white-space: nowrap;
}

/* Fullscreen chart x-axis scroll only */
.sku-sales-card:fullscreen .chart-container {
  position: relative;
  min-height: 360px;
  max-width: 100%;
  border-radius: 0 13px 13px 0;
  background: #ffffff;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  padding-bottom: 14px;
  scroll-behavior: smooth;
  scrollbar-width: auto;
  scrollbar-color: #2563eb #dbeafe;
}

.sku-sales-card:fullscreen .chart-scroll-inner {
  min-height: 360px;
}

.chart-virtual-space {
  position: relative;
  min-height: 360px;
}

.chart-render-window {
  position: sticky;
  left: 0;
  top: 0;
  min-height: 360px;
  will-change: auto;
}

/* Hide Apex native y-axis in fullscreen because fixed custom y-axis is used */
.sku-sales-card:fullscreen .chart-container :deep(.apexcharts-yaxis),
.sku-sales-card:fullscreen .chart-container :deep(.apexcharts-yaxis-texts-g),
.sku-sales-card:fullscreen .chart-container :deep(.apexcharts-yaxis-title) {
  display: none !important;
}

.sku-sales-card:fullscreen .chart-container.has-horizontal-scroll::after {
  content: "";
  position: sticky;
  right: 0;
  top: 0;
  float: right;
  width: 32px;
  height: 100%;
  z-index: 20;
  pointer-events: none;
  background: linear-gradient(270deg, rgba(255, 255, 255, 0.96), transparent);
}

.sku-sales-card:fullscreen .chart-container::-webkit-scrollbar {
  height: 9px;
}

.sku-sales-card:fullscreen .chart-container::-webkit-scrollbar-track {
  background: #dbeafe;
  border-radius: 999px;
}

.sku-sales-card:fullscreen .chart-container::-webkit-scrollbar-thumb {
  background: linear-gradient(90deg, #2563eb 0%, #14b8a6 100%);
  border-radius: 999px;
  border: 2px solid #dbeafe;
}

.sku-sales-card:fullscreen .chart-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(90deg, #1d4ed8 0%, #0f766e 100%);
}

.sku-sales-card:fullscreen .chart-container::-webkit-scrollbar-corner {
  background: transparent;
}

/* Fullscreen scroll hint */
.chart-scroll-hint {
  position: absolute;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  z-index: 45;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.94);
  color: #ffffff;
  font-size: 12px;
  font-weight: 850;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.28);
  pointer-events: none;
  animation: scrollHintSoftFloat 1.25s ease-in-out infinite;
}

.scroll-hand {
  font-size: 14px;
  color: #bfdbfe;
  animation: handSwipeMotion 1.1s ease-in-out infinite;
}

.scroll-arrow {
  font-size: 12px;
  color: #93c5fd;
  animation: arrowNudgeRight 1.1s ease-in-out infinite;
}

@keyframes scrollHintSoftFloat {
  0% {
    transform: translateX(-50%) translateY(0);
    opacity: 0.86;
  }

  50% {
    transform: translateX(-50%) translateY(-3px);
    opacity: 1;
  }

  100% {
    transform: translateX(-50%) translateY(0);
    opacity: 0.86;
  }
}

@keyframes handSwipeMotion {
  0% {
    transform: translateX(-3px) rotate(-8deg);
  }

  50% {
    transform: translateX(5px) rotate(6deg);
  }

  100% {
    transform: translateX(-3px) rotate(-8deg);
  }
}

@keyframes arrowNudgeRight {
  0% {
    transform: translateX(0);
    opacity: 0.55;
  }

  50% {
    transform: translateX(5px);
    opacity: 1;
  }

  100% {
    transform: translateX(0);
    opacity: 0.55;
  }
}

/* Fullscreen slice render loader */
.chart-render-loader {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 74px;
  z-index: 55;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 252, 0.66);
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.chart-render-loader-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.92);
  color: #ffffff;
  font-size: 12px;
  font-weight: 850;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.24);
}

.chart-render-loader-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #38bdf8;
  animation: renderDotPulse 0.8s ease-in-out infinite;
}

@keyframes renderDotPulse {
  0% {
    transform: scale(0.75);
    opacity: 0.45;
  }

  50% {
    transform: scale(1.3);
    opacity: 1;
  }

  100% {
    transform: scale(0.75);
    opacity: 0.45;
  }
}

/* Preview hint tag */
.preview-hint-tag {
  width: fit-content;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: -6px 0 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 11.5px;
  font-weight: 750;
  line-height: 1.2;
}

.preview-hint-tag i {
  font-size: 11px;
  color: #f97316;
}

.multiselect-toggle.form-select {
  background-image: none !important;
  padding-right: 12px;
}

@media (max-width: 576px) {
  .preview-hint-tag {
    width: 100%;
    align-items: flex-start;
    border-radius: 12px;
    line-height: 1.35;
  }

  .sku-sales-card:fullscreen .chart-body-frame {
    padding-left: 64px;
  }

  .fixed-y-axis {
    width: 64px;
  }

  .fixed-y-axis-title {
    font-size: 10px;
    padding-right: 7px;
  }

  .fixed-y-axis-labels {
    padding-right: 7px;
  }

  .fixed-y-axis-labels span {
    font-size: 10.5px;
  }

  .chart-scroll-hint {
    bottom: 28px;
    max-width: calc(100% - 34px);
    white-space: normal;
    text-align: center;
    justify-content: center;
    border-radius: 14px;
    line-height: 1.25;
  }

  .chart-render-loader {
    left: 64px;
  }
}
</style>